import { Link } from 'react-router-dom';
import { ChevronDown, ExternalLink } from 'lucide-react';
import { useState } from 'react';
import Seo from '../Seo';
import JsonLd from '../JsonLd';
import AdUnit from '../AdUnit';
import ToolBanner from './ToolBanner';
import { LIVE_TOOLS } from '../../data/tools';
import { getToolBanner } from '../../data/toolBanners';

const SITE_URL = 'https://pixelutility.com';

export default function ToolLandingTemplate({
  seo,
  tool,
  hero,
  features = [],
  howItWorks = [],
  longContent = [],
  faq = [],
  examples = null,
  useCases = [],
  comparisonLinks = [],
}) {
  const [openFaq, setOpenFaq] = useState(null);
  const Icon = tool.icon;
  const canonicalPath = tool.landingPath;
  const banner = getToolBanner(tool.id);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faq.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };

  const softwareSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: tool.name,
    applicationCategory: 'UtilitiesApplication',
    operatingSystem: 'Web Browser',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    url: tool.launchUrl,
    description: tool.description,
    provider: { '@type': 'Organization', name: 'PixelUtility', url: SITE_URL },
    ...(banner && {
      image: banner.ogImage,
      screenshot: banner.ogImage,
    }),
    featureList: features.map((f) => f.title),
  };

  const imageSchema = banner
    ? {
        '@context': 'https://schema.org',
        '@type': 'ImageObject',
        '@id': `${SITE_URL}${canonicalPath}#hero-banner`,
        url: banner.ogImage,
        contentUrl: banner.ogImage,
        width: banner.width,
        height: banner.height,
        caption: banner.caption,
        name: banner.title,
        description: banner.alt,
        inLanguage: 'en',
        representativeOfPage: true,
      }
    : null;

  const pageUrl = `${SITE_URL}${canonicalPath}`;

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${pageUrl}#webpage`,
    url: pageUrl,
    name: seo.title,
    description: seo.description,
    inLanguage: 'en',
    isPartOf: { '@id': `${SITE_URL}/#website` },
    ...(banner && { primaryImageOfPage: { '@id': `${pageUrl}#hero-banner` } }),
    about: { '@type': 'SoftwareApplication', name: tool.name, url: tool.launchUrl },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Tools', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 3, name: tool.name, item: pageUrl },
    ],
  };

  const otherTools = LIVE_TOOLS.filter((t) => t.id !== tool.id);

  return (
    <>
      <Seo
        {...seo}
        path={canonicalPath}
        image={banner?.ogImage}
        imageAlt={banner?.alt}
        imageWidth={banner?.width}
        imageHeight={banner?.height}
        keywords={banner?.keywords}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@graph': [webPageSchema, faqSchema, softwareSchema, breadcrumbSchema, imageSchema].filter(Boolean),
        }}
      />

      {/* Hero */}
      <section className="pt-24 pb-16 sm:pt-28 sm:pb-20 px-4 bg-gradient-to-b from-brand-50/50 to-white">
        <div className="max-w-6xl mx-auto">
          <nav className="text-sm text-gray-500 mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-brand-600">Home</Link>
            <span className="mx-2">/</span>
            <a href="/#tools" className="hover:text-brand-600">Tools</a>
            <span className="mx-2">/</span>
            <span className="text-gray-900">{tool.shortName}</span>
          </nav>

          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-100 text-brand-700 text-xs font-medium mb-4">
              <Icon size={14} />
              Free browser-based tool
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 leading-tight">
              {hero.title}
            </h1>
            <p className="mt-4 text-lg text-gray-600 leading-relaxed">{hero.subtitle}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={tool.launchUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-brand-600 to-blue-500 text-white font-semibold text-sm shadow-lg shadow-brand-500/25 hover:shadow-brand-500/40 transition-all"
              >
                Launch Tool
                <ExternalLink size={16} />
              </a>
              <a
                href="#how-it-works"
                className="inline-flex items-center px-6 py-3 rounded-full border border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-colors"
              >
                Learn More
              </a>
            </div>
          </div>

          {banner && (
            <ToolBanner banner={banner} priority className="mt-10 sm:mt-12" />
          )}
        </div>
      </section>

      {/* Features */}
      <section className="py-16 px-4 bg-gray-50/50" aria-labelledby="features-heading">
        <div className="max-w-6xl mx-auto">
          <h2 id="features-heading" className="text-2xl sm:text-3xl font-bold text-gray-900 text-center">
            Key Features
          </h2>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f) => (
              <div key={f.title} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                <h3 className="font-semibold text-gray-900">{f.title}</h3>
                <p className="mt-2 text-sm text-gray-600 leading-relaxed">{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Examples gallery */}
      {examples && (
        <section className="py-16 px-4" aria-labelledby="examples-heading">
          <div className="max-w-6xl mx-auto">
            <h2 id="examples-heading" className="text-2xl sm:text-3xl font-bold text-gray-900 text-center">
              {examples.title}
            </h2>
            <p className="mt-3 text-center text-gray-600">{examples.subtitle}</p>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {examples.items.map((item) => (
                <div key={item.name} className="rounded-2xl border border-gray-100 p-5 bg-white hover:shadow-md transition-shadow">
                  <h3 className="font-semibold text-gray-900">{item.name}</h3>
                  <p className="mt-2 text-sm text-gray-500">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Use cases */}
      {useCases.length > 0 && (
        <section className="py-16 px-4 bg-gray-50/50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center">Popular Use Cases</h2>
            <ul className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
              {useCases.map((uc) => (
                <li key={uc} className="flex items-start gap-3 text-gray-700">
                  <span className="mt-1.5 w-2 h-2 rounded-full bg-brand-500 shrink-0" />
                  {uc}
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* How it works */}
      <section id="how-it-works" className="py-16 px-4 border-t border-gray-100">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center">How It Works</h2>
          <ol className="mt-10 space-y-6">
            {howItWorks.map((step, i) => (
              <li key={step.title} className="flex gap-4">
                <span className="flex-shrink-0 w-10 h-10 rounded-full bg-brand-100 text-brand-700 font-bold flex items-center justify-center">
                  {i + 1}
                </span>
                <div>
                  <h3 className="font-semibold text-gray-900">{step.title}</h3>
                  <p className="mt-1 text-gray-600 text-sm leading-relaxed">{step.description}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 py-6">
        <AdUnit slot="3659288415" />
      </div>

      {/* Long SEO content */}
      <article className="py-16 px-4 border-t border-gray-100">
        <div className="max-w-3xl mx-auto prose prose-gray prose-headings:text-gray-900 prose-a:text-brand-600">
          {longContent.map((section) => (
            <section key={section.id} className="mb-10">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">{section.title}</h2>
              {section.paragraphs.map((p, i) => (
                <p key={i} className="text-gray-600 leading-relaxed mb-4 text-base">
                  {p}
                </p>
              ))}
              {section.list && (
                <ul className="list-disc pl-6 space-y-2 text-gray-600 mb-4">
                  {section.list.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>
      </article>

      {/* Comparisons internal links */}
      {comparisonLinks.length > 0 && (
        <section className="py-12 px-4 bg-gray-50/50">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-lg font-bold text-gray-900">Related Guides</h2>
            <ul className="mt-4 space-y-2">
              {comparisonLinks.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-brand-600 hover:underline font-medium">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="py-16 px-4 border-t border-gray-100" aria-labelledby="faq-heading">
        <div className="max-w-3xl mx-auto">
          <h2 id="faq-heading" className="text-2xl sm:text-3xl font-bold text-gray-900 text-center">
            Frequently Asked Questions
          </h2>
          <div className="mt-10 space-y-3">
            {faq.map((item, i) => (
              <div key={item.q} className="border border-gray-100 rounded-xl overflow-hidden bg-white">
                <button
                  type="button"
                  className="w-full flex items-center justify-between px-5 py-4 text-left font-medium text-gray-900 hover:bg-gray-50"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                >
                  {item.q}
                  <ChevronDown size={18} className={`shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4 text-gray-600 text-sm leading-relaxed">{item.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Internal links */}
      <section className="py-12 px-4 bg-gray-50/50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-lg font-bold text-gray-900 text-center">More Free Tools</h2>
          <div className="mt-6 flex flex-wrap justify-center gap-4">
            {otherTools.map((t) => (
              <Link
                key={t.id}
                to={t.landingPath}
                className="px-4 py-2 rounded-full bg-white border border-gray-200 text-sm font-medium text-gray-700 hover:border-brand-300 hover:text-brand-600 transition-colors"
              >
                {t.name}
              </Link>
            ))}
            <Link to="/blog" className="px-4 py-2 rounded-full bg-white border border-gray-200 text-sm font-medium text-gray-700 hover:border-brand-300 hover:text-brand-600">
              Blog
            </Link>
            <Link to="/support" className="px-4 py-2 rounded-full bg-white border border-gray-200 text-sm font-medium text-gray-700 hover:border-brand-300 hover:text-brand-600">
              Support
            </Link>
            <Link to="/privacy" className="px-4 py-2 rounded-full bg-white border border-gray-200 text-sm font-medium text-gray-700 hover:border-brand-300 hover:text-brand-600">
              Privacy
            </Link>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 px-4 bg-gradient-to-r from-brand-600 to-blue-500 text-white text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold">Ready to try {tool.shortName}?</h2>
          <p className="mt-3 text-white/90">Open the full tool on its dedicated subdomain — free, no sign-up.</p>
          <a
            href={tool.launchUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 px-8 py-3 rounded-full bg-white text-brand-700 font-semibold shadow-lg hover:bg-gray-50 transition-colors"
          >
            Open Tool
            <ExternalLink size={18} />
          </a>
        </div>
      </section>
    </>
  );
}
